import { useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { logger } from '../utils/logger';

import { memoriserOrigineParent } from '../utils/parentOrigin';
import { ajouterPhotosRecues } from '../utils/photosJointes';

// Allowed origins for postMessage communication
const ALLOWED_ORIGINS = [
  window.location.origin,
  API_URL,
].filter(Boolean);

export function isAllowedOrigin(origin) {
  // Allow same-origin and chrome-extension:// origins (for sidepanel)
  if (ALLOWED_ORIGINS.includes(origin)) { memoriserOrigineParent(origin); return true; }
  if (origin.startsWith('chrome-extension://')) { memoriserOrigineParent(origin); return true; }
  // Doctolib (panneau iframe injecté par content.js) — match STRICT sur le
  // hostname. `includes('doctolib.fr')` acceptait « doctolib.fr.attaquant.com ».
  try {
    const h = new URL(origin).hostname;
    if (h === 'doctolib.fr' || h.endsWith('.doctolib.fr')) { memoriserOrigineParent(origin); return true; }
  } catch (_) { /* origine non parsable → refus */ }
  return false;
}

/**
 * Custom hook to handle all postMessage communication with the Chrome extension side panel.
 * All setter functions and refs are stored in a stable ref to avoid stale closures.
 */
export function useExtensionMessages({
  isInIframe,
  location,
  setPatientEmail,
  setPatientPhone,
  setDoctoSource,
  setNotes,
  setClinicalContext,
  setTranscript,
  setPhotos,
  setCompactMode,
  setExtensionAutoStart,
  setIsProcessing,
  setError,
  handleProcessRef,
  recorderRef,
  patientEmail,
  patientPhone,
  notes,
  transcript,
  photos,
}) {
  // Stable ref for all setters (React setState functions are stable, but we consolidate for clarity)
  const settersRef = useRef({
    setPatientEmail, setPatientPhone, setDoctoSource,
    setNotes, setClinicalContext, setTranscript, setPhotos,
    setCompactMode, setExtensionAutoStart,
    setIsProcessing, setError,
  });
  useEffect(() => {
    settersRef.current = {
      setPatientEmail, setPatientPhone, setDoctoSource,
      setNotes, setClinicalContext, setTranscript, setPhotos,
      setCompactMode, setExtensionAutoStart,
      setIsProcessing, setError,
    };
  });

  // Stable ref for current state values (needed for state request response)
  const stateRef = useRef({ patientEmail, patientPhone, notes, transcript, photos });
  useEffect(() => {
    stateRef.current = { patientEmail, patientPhone, notes, transcript, photos };
  });

  // Handle URL query params from extension "Open in tab" button — mount only
  useEffect(() => {
    const s = settersRef.current;
    const params = new URLSearchParams(location.search);
    if (params.get('email')) s.setPatientEmail(params.get('email'));
    if (params.get('phone')) s.setPatientPhone(params.get('phone'));
    if (params.get('notes')) s.setNotes(prev => prev ? prev + '\n' + params.get('notes') : params.get('notes'));
    if (params.get('transcript')) s.setTranscript(prev => prev ? prev + ' ' + params.get('transcript') : params.get('transcript'));
    if (params.get('email') || params.get('phone')) s.setDoctoSource('doctolib');
    if (params.toString()) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (isInIframe) {
      // Parent is chrome-extension:// — use referrer origin when available
      let extensionOrigin;
      try { if (document.referrer) extensionOrigin = new URL(document.referrer).origin; } catch (e) { logger.warn('[MediPocket] Could not parse referrer origin:', e?.message); }
      window.parent.postMessage({ type: 'MEDIPOCKET_READY' }, extensionOrigin || '*');
    }
  }, [isInIframe, location.search]);

  // Handle state request from side panel ("Ouvrir en grand" button)
  useEffect(() => {
    const handleStateRequest = (event) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (event.data?.type !== 'MEDIPOCKET_GET_STATE') return;
      const st = stateRef.current;
      window.parent.postMessage({
        type: 'MEDIPOCKET_STATE_RESPONSE',
        state: {
          email: st.patientEmail || '',
          phone: st.patientPhone || '',
          patientName: '',
          notes: st.notes || '',
          transcript: st.transcript || '',
          photos: st.photos.map(p => ({ id: p.id, name: p.name, data: p.data })),
        }
      }, event.origin);
    };
    window.addEventListener('message', handleStateRequest);
    return () => window.removeEventListener('message', handleStateRequest);
  }, []);

  // Handle "Stop & Generate" signal from extension FAB
  useEffect(() => {
    const handleStopGenerate = (event) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (event.data?.type !== 'MEDIPOCKET_STOP_AND_GENERATE') return;
      if (recorderRef.current?.isRecording) {
        recorderRef.current.stopRecording();
      }
      setTimeout(() => { handleProcessRef.current?.(); }, 1000);
    };
    window.addEventListener('message', handleStopGenerate);
    return () => window.removeEventListener('message', handleStopGenerate);
  }, [recorderRef, handleProcessRef]);

  // Listen for Doctolib extension messages
  const handleExtensionData = useCallback(async (event) => {
    if (!isAllowedOrigin(event.origin)) return;
    const data = event.data;
    if (!data || data.type !== 'MEDIPOCKET_EXTENSION_DATA') return;
    const s = settersRef.current;

    s.setCompactMode(true);
    if (data.autoStart) s.setExtensionAutoStart(true);
    if (data.email) s.setPatientEmail(data.email);
    if (data.phone) s.setPatientPhone(data.phone);
    if (data.email || data.phone) s.setDoctoSource('doctolib');

    // Build medical context: use the doctor-edited medicalContext if available, else build from fields
    let contextText = '';
    if (data.medicalContext && data.medicalContext.trim()) {
      contextText = data.medicalContext.trim();
    } else {
      const contextParts = [];
      if (data.sex || data.age) {
        const sexLabel = data.sex === 'F' ? 'Patiente' : data.sex === 'M' ? 'Patient' : 'Patient(e)';
        contextParts.push(data.age ? `${sexLabel} de ${data.age} ans` : sexLabel);
      }
      if (data.antecedents) contextParts.push(`ATCD : ${data.antecedents}`);
      if (data.allergies) contextParts.push(`Allergies : ${data.allergies}`);
      if (data.traitements) contextParts.push(`TTT de fond : ${data.traitements}`);
      // Vitals
      const vitals = [];
      if (data.pas && data.pad) vitals.push(`PAS ${data.pas} mmHg, PAD ${data.pad} mmHg`);
      else if (data.tension) vitals.push(`TA ${data.tension}`);
      if (data.pouls) vitals.push(`FC ${data.pouls} bpm`);
      if (data.temperature) vitals.push(`T° ${data.temperature}°C`);
      if (data.saturation) vitals.push(`SpO2 ${data.saturation}%`);
      if (data.poids) vitals.push(`Poids ${data.poids} kg`);
      if (data.taille) vitals.push(`Taille ${data.taille} cm`);
      if (data.imc) vitals.push(`IMC ${data.imc}`);
      if (data.iah) vitals.push(`IAH ${data.iah}`);
      if (vitals.length > 0) contextParts.push(`Constantes : ${vitals.join(', ')}`);
      // Motif + Interrogatoire + Examen + Conclusion
      if (data.motif) contextParts.push(`Motif : ${data.motif}`);
      if (data.interrogatoire) contextParts.push(`Interrogatoire : ${data.interrogatoire}`);
      if (data.examenClinique) contextParts.push(`Examen clinique : ${data.examenClinique}`);
      if (data.conclusion) contextParts.push(`Conclusion : ${data.conclusion}`);
      if (contextParts.length > 0) contextText = contextParts.join('\n');
    }

    // Set clinical context separately (sent as dedicated field to Claude)
    if (contextText) {
      logger.info('[MediPocket] Setting clinical context:', contextText.slice(0, 100));
      s.setClinicalContext(contextText);
    } else {
      logger.info('[MediPocket] No clinical context found. medicalContext=', data.medicalContext?.slice(0, 50), 'sex=', data.sex, 'age=', data.age);
    }

    // Notes from extension (manual notes only, not mixed with context)
    if (data.notes) s.setNotes(prev => prev ? prev + '\n' + data.notes : data.notes);

    if (data.photos && data.photos.length > 0) {
      // L'extension pousse la session ENTIÈRE à chaque fois : sans le filtre
      // par identifiant, une seconde poussée doublait les photos déjà jointes.
      s.setPhotos(prev => ajouterPhotosRecues(prev, data.photos, { fromExtension: true }));
    }

    // Pass contextText directly to handleProcess to avoid React timing issues
    const hasLiveTranscript = data.liveTranscript && data.liveTranscript.trim().length > 0;

    if (hasLiveTranscript) {
      s.setTranscript(prev => prev ? prev + ' ' + data.liveTranscript : data.liveTranscript);
      setTimeout(() => { handleProcessRef.current?.(contextText || null); }, 500);
    } else if (data.audioBase64) {
      try {
        const byteChars = atob(data.audioBase64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: 'audio/webm' });
        const file = new File([blob], 'extension-recording.webm', { type: 'audio/webm' });

        s.setIsProcessing(true);
        s.setError('');
        const fd = new FormData();
        fd.append('audio', file);
        const { data: result } = await axios.post(`${API_URL}/api/transcription/audio`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (result.transcript) {
          s.setTranscript(prev => prev ? prev + ' ' + result.transcript : result.transcript);
        }
        s.setIsProcessing(false);
        setTimeout(() => { handleProcessRef.current?.(contextText || null); }, 500);
      } catch (err) {
        logger.error('Extension audio processing failed:', err);
        s.setError('Erreur de transcription audio depuis l\'extension');
        s.setIsProcessing(false);
      }
    } else if (data.notes && data.notes.trim()) {
      setTimeout(() => { handleProcessRef.current?.(contextText || null); }, 500);
    }
  }, [handleProcessRef]);

  useEffect(() => {
    window.addEventListener('message', handleExtensionData);
    return () => window.removeEventListener('message', handleExtensionData);
  }, [handleExtensionData]);

  // Listen for real-time transcript updates from side panel native recording
  useEffect(() => {
    const handleTranscriptUpdate = (event) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (!event.data || event.data.type !== 'MEDIPOCKET_TRANSCRIPT_UPDATE') return;
      if (event.data.transcript) {
        settersRef.current.setTranscript(event.data.transcript);
      }
    };
    window.addEventListener('message', handleTranscriptUpdate);
    return () => window.removeEventListener('message', handleTranscriptUpdate);
  }, []);

  // Push: receive continuous context updates from content.js via sidepanel
  useEffect(() => {
    const handleContextUpdate = (event) => {
      if (!isAllowedOrigin(event.origin)) return;
      const data = event.data;
      if (!data || (data.type !== 'MEDIPOCKET_CONTEXT_UPDATE' && data.type !== 'MEDIPOCKET_FRESH_CONTEXT')) return;

      const s = settersRef.current;
      // Rebuild clinical context from updated fields
      const contextParts = [];
      if (data.sex || data.age) {
        const sexLabel = data.sex === 'F' ? 'Patiente' : data.sex === 'M' ? 'Patient' : 'Patient(e)';
        contextParts.push(data.age ? `${sexLabel} de ${data.age} ans` : sexLabel);
      }
      if (data.antecedents) contextParts.push(`ATCD : ${data.antecedents}`);
      if (data.allergies) contextParts.push(`Allergies : ${data.allergies}`);
      if (data.traitements) contextParts.push(`TTT de fond : ${data.traitements}`);
      const vitals = [];
      if (data.pas && data.pad) vitals.push(`PAS ${data.pas} mmHg, PAD ${data.pad} mmHg`);
      else if (data.tension) vitals.push(`TA ${data.tension}`);
      if (data.pouls) vitals.push(`FC ${data.pouls} bpm`);
      if (data.temperature) vitals.push(`T° ${data.temperature}°C`);
      if (data.saturation) vitals.push(`SpO2 ${data.saturation}%`);
      if (data.poids) vitals.push(`Poids ${data.poids} kg`);
      if (data.taille) vitals.push(`Taille ${data.taille} cm`);
      if (data.imc) vitals.push(`IMC ${data.imc}`);
      if (data.iah) vitals.push(`IAH ${data.iah}`);
      if (vitals.length > 0) contextParts.push(`Constantes : ${vitals.join(', ')}`);
      if (data.motif) contextParts.push(`Motif : ${data.motif}`);
      if (data.interrogatoire) contextParts.push(`Interrogatoire : ${data.interrogatoire}`);
      if (data.examenClinique) contextParts.push(`Examen clinique : ${data.examenClinique}`);
      if (data.conclusion) contextParts.push(`Conclusion : ${data.conclusion}`);

      if (contextParts.length > 0) {
        const contextText = contextParts.join('\n');
        s.setClinicalContext(contextText);
        logger.info('[MediPocket] Context updated:', contextText.slice(0, 80));
      }

      // Handle live transcript updates from recording finalization
      if (data.liveTranscript && data.liveTranscript.trim()) {
        s.setTranscript(data.liveTranscript);
        logger.info('[MediPocket] Transcript updated:', data.liveTranscript.slice(0, 80));
      }
    };

    // Handle session clear (extension panel closed)
    const handleClearSession = (event) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (event.data?.type === 'MEDIPOCKET_CLEAR_SESSION') {
        logger.info('[MediPocket] Session cleared by extension');
        try { localStorage.removeItem('mp_autosave'); } catch (err) { logger.warn('[MediPocket] Autosave clear failed:', err?.message); }
        try { localStorage.removeItem('medipocket_transcript_backup'); } catch (err) { logger.warn('[MediPocket] Transcript backup clear failed:', err?.message); }
      }
    };

    window.addEventListener('message', handleContextUpdate);
    window.addEventListener('message', handleClearSession);
    return () => {
      window.removeEventListener('message', handleContextUpdate);
      window.removeEventListener('message', handleClearSession);
    };
  }, []);
}
